import { Dispatch } from "@reduxjs/toolkit";
import { workLocationsAPI, WorkLocation } from "../api/workLocationsAPI";
import {
  setUserWorkLocations,
  setClientLocations,
} from "../redux/slices/workLocationsSlice";

/**
 * Helper function to get user work locations
 * First checks Redux state, if not found calls API and caches in Redux
 *
 * @param userId - The ID of the user
 * @param accessToken - The access token for API authentication
 * @param dispatch - Redux dispatch function
 * @param workLocationsState - Current Redux state for work locations
 * @param forceRefresh - Force API call even if data exists in Redux (default: false)
 * @returns Promise<WorkLocation[]>
 */
export const getUserWorkLocationsHelper = async (
  userId: string,
  accessToken: string,
  dispatch: Dispatch,
  workLocationsState: { userLocations: { [userId: string]: WorkLocation[] } },
  forceRefresh: boolean = false,
): Promise<WorkLocation[]> => {
  // If forceRefresh is false and we have data in Redux, return it
  if (!forceRefresh && workLocationsState.userLocations[userId]) {
    return workLocationsState.userLocations[userId];
  }

  // Otherwise, fetch from API
  try {
    const response = await workLocationsAPI.getUserWorkLocations(
      userId,
      accessToken,
    );
    const locations = response.workLocations || [];

    // Cache in Redux
    dispatch(setUserWorkLocations({ userId, locations }));

    return locations;
  } catch (error) {
    console.error("Error loading work locations:", error);
    return [];
  }
};

/**
 * Helper function to get all locations for a client
 * First checks Redux state, if not found calls API and caches in Redux
 *
 * @param clientId - The ID of the client
 * @param accessToken - The access token for API authentication
 * @param dispatch - Redux dispatch function
 * @param workLocationsState - Current Redux state for work locations
 * @param forceRefresh - Force API call even if data exists in Redux (default: false)
 * @returns Promise<WorkLocation[]>
 */
export const getClientLocationsHelper = async (
  clientId: string,
  accessToken: string,
  dispatch: Dispatch,
  workLocationsState: {
    clientLocations: { [clientId: string]: WorkLocation[] };
  },
  forceRefresh: boolean = false,
): Promise<WorkLocation[]> => {
  // If forceRefresh is false and we have data in Redux, return it
  if (!forceRefresh && workLocationsState.clientLocations[clientId]) {
    return workLocationsState.clientLocations[clientId];
  }

  // Otherwise, fetch from API
  try {
    const locations = await workLocationsAPI.getClientLocations(
      clientId,
      accessToken,
    );

    // Cache in Redux
    dispatch(setClientLocations({ clientId, locations }));

    return locations;
  } catch (error) {
    console.error("Error loading client locations:", error);
    return [];
  }
};
